"use client";

import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import {
  getAllLanguages,
  getLanguageFromPath,
  type SupportedLanguage
} from "@/lib/language";
import { useLocation } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "./select";

interface LanguageToggleProps {
  className?: string;
}

const languageInfo = {
  en: { flag: "🇬🇧", name: "EN" },
  sv: { flag: "🇸🇪", name: "SV" },
  es: { flag: "🇪🇸", name: "ES" },
};

export function LanguageToggle({ className }: LanguageToggleProps) {
  const { i18n } = useTranslation();
  const location = useLocation();
  const currentLang = getLanguageFromPath(location.pathname) as SupportedLanguage;

  const handleLanguageChange = (newLang: string) => {
    if (newLang !== currentLang) {
      i18n.changeLanguage(newLang);
    }
  };

  return (
    <Select value={currentLang} onValueChange={handleLanguageChange}>
      <SelectTrigger
        className={cn(
          "rounded-sm font-link text-white bg-black px-4 py-4 md:py-5.5 cursor-pointer",
          "2k:px-6 2k:py-9 2k:rounded-md",
          "4k:px-12 4k:py-14 4k:rounded-xl",
          className,
        )}
      >
        <SelectValue>
          <div className="flex items-center gap-2">
            <span className="text-lg 2k:text-2xl 4k:text-4xl">
              {languageInfo[currentLang].flag}
            </span>
            <span>{languageInfo[currentLang].name}</span>
          </div>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {Object.entries(languageInfo).map(([code, info]) => (
          <SelectItem key={code} value={code}>
            <div className="flex items-center gap-2">
              <span>{info.flag}</span>
              <span>{info.name}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
